export type ContactIntent =
    | 'contact'
    | 'demo'
    | 'start'
    | 'access'
    | 'partner'
    | 'human'
    | 'invite'
    | 'expert'
    | 'updates'
    | 'blog'
    | 'changelog'

export interface ContactIntentConfig {
    label: string
    title: string
    description: string
    submit: string
    compact?: boolean
    messageLabel?: string
    messagePlaceholder?: string
}

export const contactIntents: Record<ContactIntent, ContactIntentConfig> = {
    contact: {
        label: "Contact request",
        title: "Talk to our team",
        description: "Tell us which systems you run and what should move between them. A solutions engineer will follow up by email.",
        submit: "Send message",
        messageLabel: "What do you want to connect?",
        messagePlaceholder: "For example: sync Shopify orders into NetSuite and post shipping updates back."
    },
    demo: {
        label: "Demo request",
        title: "Book a demo",
        description: "See FlowCraft working on your own workflows. Share a few details and we will set up a time that suits you.",
        submit: "Request a demo",
        messageLabel: "Anything we should prepare?",
        messagePlaceholder: "The systems you use and the work you would like to automate."
    },
    start: {
        label: "Getting started",
        title: "Get started with FlowCraft",
        description: "Tell us about your stack and we will set up your workspace with you.",
        submit: "Get started",
        messageLabel: "What would you like to automate first?",
        messagePlaceholder: "For example: post new HubSpot deals to Slack."
    },
    access: {
        label: "Workspace access",
        title: "Access your workspace",
        description: "FlowCraft workspaces are set up together with our team. Leave your work email and we will help you get in.",
        submit: "Request access",
        messageLabel: "Anything we should know?",
        messagePlaceholder: "Your company or the workspace you are trying to reach."
    },
    partner: {
        label: "Partnership inquiry",
        title: "Partner with FlowCraft",
        description: "Agencies, consultancies and software vendors: tell us how you would like to work together.",
        submit: "Send inquiry",
        messageLabel: "How would you like to partner?",
        messagePlaceholder: "Who you serve and what your customers need connected."
    },
    human: {
        label: "Speak to a human",
        title: "Speak to a human",
        description: "No bots and no queue. Tell us what you need and a person from our team will reply.",
        submit: "Send message",
        messageLabel: "How can we help?",
        messagePlaceholder: "Ask us anything about FlowCraft."
    },
    invite: {
        label: "Invite request",
        title: "Request an invite",
        description: "Join the early access list for the app network built to cover the workflows your business actually runs.",
        submit: "Request an invite",
        messageLabel: "Which apps matter most to you?",
        messagePlaceholder: "List the tools your team relies on."
    },
    expert: {
        label: "Expert session",
        title: "Talk to an expert",
        description: "Work with a FlowCraft specialist to map the systems, actions and automations your team needs next.",
        submit: "Talk to an expert",
        messageLabel: "What are you trying to solve?",
        messagePlaceholder: "The systems involved and where work gets stuck today."
    },
    updates: {
        label: "Updates signup",
        title: "Get updates",
        description: "New app coverage, product launches and practical integration notes from the FlowCraft team.",
        submit: "Keep me posted",
        compact: true
    },
    blog: {
        label: "Blog signup",
        title: "The FlowCraft blog is on its way",
        description: "Practical notes on workflow automation are coming soon. Leave your email and we will send the first posts your way.",
        submit: "Notify me",
        compact: true
    },
    changelog: {
        label: "Changelog signup",
        title: "The changelog is on its way",
        description: "A running record of everything we ship is coming soon. Leave your email to hear about new releases.",
        submit: "Notify me",
        compact: true
    }
}

export const isContactIntent = (value: unknown): value is ContactIntent => typeof value === 'string' && value in contactIntents
